import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import AddServicePopup from '../../components/feature/AddServicePopup';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CustomerProfile {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  number_of_cans: number;
  can_color?: string;
  special_preferences?: string;
}

interface Subscription {
  id: string;
  status: string;
  plan: string;
  price: number;
  interval: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  additional_cans?: number;
}

interface PaymentRecord {
  id: string;
  date: string;
  amount: number;
  status: string;
  plan: string;
  type: string;
  invoiceUrl: string | null;
  invoicePdf: string | null;
}

interface SubscriptionHistoryRecord {
  id: string;
  plan: string;
  planType: string;
  status: string;
  startDate: string;
  endDate: string | null;
  canceledAt: string | null;
  currentPeriodEnd: string;
}

export default function AccountPage() {
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [formData, setFormData] = useState<CustomerProfile | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showModifyOptions, setShowModifyOptions] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState<string | null>(null);
  const [newAdditionalCans, setNewAdditionalCans] = useState(0);
  const [isAddServicePopupOpen, setIsAddServicePopupOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState<SubscriptionHistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState<'payments' | 'subscriptions'>('payments');

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      window.REACT_APP_NAVIGATE('/login');
      return;
    }

    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
    }

    if (!data) {
      // No profile exists - check if there's pending profile data
      const pendingProfileStr = localStorage.getItem('pendingProfile');
      let newProfile: CustomerProfile;

      if (pendingProfileStr) {
        // Use the data from signup
        const pendingProfile = JSON.parse(pendingProfileStr);
        newProfile = {
          id: '',
          full_name: pendingProfile.fullName || '',
          email: user.email || '',
          phone: pendingProfile.phone || '',
          address: pendingProfile.address || '',
          city: pendingProfile.city || '',
          state: pendingProfile.state || '',
          zip_code: pendingProfile.zipCode || '',
          number_of_cans: pendingProfile.numberOfCans || 2,
          can_color: '',
          special_preferences: ''
        };

        // Automatically try to create the profile
        try {
          const { data: insertedData, error: insertError } = await supabase
            .from('customers')
            .insert([{
              user_id: user.id,
              full_name: newProfile.full_name,
              email: newProfile.email,
              phone: newProfile.phone,
              address: newProfile.address,
              city: newProfile.city,
              state: newProfile.state,
              zip_code: newProfile.zip_code,
              number_of_cans: newProfile.number_of_cans
            }])
            .select()
            .single();

          if (!insertError && insertedData) {
            // Success! Clear localStorage and set profile
            localStorage.removeItem('pendingProfile');
            setProfile(insertedData);
            setFormData(insertedData);
            setIsNewProfile(false);
            setStatus('success');
            setMessage('Profile created successfully!');
            setTimeout(() => setStatus('idle'), 3000);
          } else {
            throw insertError;
          }
        } catch (err) {
          console.error('Failed to auto-create profile:', err);
          // If auto-create fails, let user manually save
          setProfile(newProfile);
          setFormData(newProfile);
          setIsNewProfile(true);
          setIsEditing(true);
          setStatus('error');
          setMessage('Please save your profile information below.');
        }
      } else {
        // No pending data - create empty profile
        newProfile = {
          id: '',
          full_name: '',
          email: user.email || '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          number_of_cans: 2,
          can_color: '',
          special_preferences: ''
        };
        setProfile(newProfile);
        setFormData(newProfile);
        setIsNewProfile(true);
        setIsEditing(true);
      }
    } else {
      // Profile exists
      localStorage.removeItem('pendingProfile'); // Clean up any old data
      setProfile(data);
      setFormData(data);
      setIsNewProfile(false);
    }

    // Fetch subscription information
    await fetchSubscription(user.email || '');

    setLoading(false);
  };

  const fetchSubscription = async (email: string) => {
    setSubscriptionLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      
      if (data.subscription) {
        setSubscription(data.subscription);
        setSelectedNewPlan(data.subscription.plan);
        setNewAdditionalCans(data.subscription.additional_cans || 0);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }

    // Also fetch history
    await fetchHistory(email);
  };

  const fetchHistory = async (email: string) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-subscription-history`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      
      if (data.payments) {
        setPaymentHistory(data.payments);
      }
      if (data.subscriptionHistory) {
        setSubscriptionHistory(data.subscriptionHistory);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription || !window.confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
      return;
    }

    setSubscriptionLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ subscriptionId: subscription.id }),
        }
      );

      const data = await response.json();
      
      if (data.success) {
        await fetchSubscription(profile?.email || '');
        setStatus('success');
        setMessage('Subscription cancelled successfully. You will have access until the end of your billing period.');
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setStatus('error');
      setMessage('Failed to cancel subscription. Please try again.');
      setTimeout(() => setStatus('idle'), 5000);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedNewPlan || !subscription) return;

    setSubscriptionLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            subscriptionId: subscription.id,
            newPlanType: selectedNewPlan,
            additionalCans: newAdditionalCans,
          }),
        }
      );

      const data = await response.json();
      
      if (data.success) {
        await fetchSubscription(profile?.email || '');
        setShowModifyOptions(false);
        setStatus('success');
        setMessage('Subscription updated successfully! Changes will be prorated on your next bill.');
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error(data.error || 'Failed to update subscription');
      }
    } catch (error) {
      console.error('Update subscription error:', error);
      setStatus('error');
      setMessage('Failed to update subscription. Please try again.');
      setTimeout(() => setStatus('idle'), 5000);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleModifySubscription = async () => {
    if (!selectedNewPlan) return;

    setSubscriptionLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            planType: selectedNewPlan,
            additionalCans: newAdditionalCans,
            successUrl: `${window.location.origin}/?payment=success`,
            cancelUrl: `${window.location.origin}/account`,
          }),
        }
      );

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Modify subscription error:', error);
      setStatus('error');
      setMessage('Failed to modify subscription. Please try again.');
      setTimeout(() => setStatus('idle'), 5000);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleStartSubscription = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('pendingProfile');
    window.REACT_APP_NAVIGATE('/');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (formData) {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('saving');
    setMessage('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      if (isNewProfile) {
        // Insert new profile
        const { data: insertedData, error } = await supabase
          .from('customers')
          .insert([{
            user_id: user.id,
            full_name: formData?.full_name,
            email: formData?.email,
            phone: formData?.phone,
            address: formData?.address,
            city: formData?.city,
            state: formData?.state,
            zip_code: formData?.zip_code,
            number_of_cans: formData?.number_of_cans,
            can_color: formData?.can_color,
            special_preferences: formData?.special_preferences
          }])
          .select()
          .single();

        if (error) throw error;

        localStorage.removeItem('pendingProfile');
        setProfile(insertedData);
        setFormData(insertedData);
        setIsNewProfile(false);
      } else {
        // Update existing profile
        const { error } = await supabase
          .from('customers')
          .update({
            full_name: formData?.full_name,
            phone: formData?.phone,
            address: formData?.address,
            city: formData?.city,
            state: formData?.state,
            zip_code: formData?.zip_code,
            number_of_cans: formData?.number_of_cans,
            can_color: formData?.can_color,
            special_preferences: formData?.special_preferences,
            updated_at: new Date().toISOString()
          })
          .eq('id', profile?.id);

        if (error) throw error;

        setProfile(formData);
      }

      setStatus('success');
      setMessage(isNewProfile ? 'Profile created successfully!' : 'Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error: unknown) {
      setStatus('error');
      if (error instanceof Error) {
        console.error('Save error:', error);
        setMessage(error.message || 'Failed to save profile. Please try again.');
      } else {
        setMessage('Failed to save profile. Please try again.');
      }
    }
  };

  const getPlanName = (plan: string) => {
    const plans: Record<string, string> = {
      'monthly': 'Monthly Clean',
      'quarterly': 'Quarterly Clean',
      'one-time': 'One-Time Clean'
    };
    return plans[plan] || plan;
  };

  const getPlanPrice = (plan: string) => {
    const prices: Record<string, number> = {
      'monthly': 20,
      'quarterly': 35,
      'one-time': 70
    };
    return prices[plan] || 0;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'canceled':
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-700';
      case 'trialing':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-yellow-50 flex items-center justify-center">
        <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-yellow-50">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl md:text-5xl font-black text-blue-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              My Account
            </h1>
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white px-6 py-2 rounded-full font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Log Out
            </button>
          </div>

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-green-100 text-green-800 px-4 py-3 rounded-2xl mb-6 text-center font-bold"
            >
              {message}
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-100 text-red-800 px-4 py-3 rounded-2xl mb-6 text-center font-bold"
            >
              {message}
            </motion.div>
          )}

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Profile Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl"
            >
              <h2 className="text-3xl font-black text-blue-600 mb-6" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Profile Information
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData?.full_name || ''}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={profile?.email || ''}
                      disabled
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 bg-gray-50 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData?.phone || ''}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Number of Cans</label>
                    <select
                      name="number_of_cans"
                      value={formData?.number_of_cans || 2}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm cursor-pointer"
                    >
                      <option value="1">1 Trash Can</option>
                      <option value="2">2 Trash Cans</option>
                      <option value="3">3 Trash Cans</option>
                      <option value="4">4+ Trash Cans</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Can Color (Optional)</label>
                    <input
                      type="text"
                      name="can_color"
                      value={formData?.can_color || ''}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="e.g., Green, Black"
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Service Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData?.address || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData?.city || ''}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData?.state || ''}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">ZIP Code</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData?.zip_code || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Special Preferences</label>
                  <textarea
                    name="special_preferences"
                    value={formData?.special_preferences || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="Any special instructions or preferences..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none disabled:bg-gray-50 resize-none text-sm"
                  ></textarea>
                </div>

                <div className="flex gap-4">
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="flex-1 bg-blue-600 text-white py-4 rounded-full font-bold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <>
                      <button
                        type="submit"
                        disabled={status === 'saving'}
                        className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                      >
                        {status === 'saving' ? 'Saving...' : 'Save Changes'}
                      </button>
                      {!isNewProfile && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditing(false);
                            setFormData(profile);
                          }}
                          className="flex-1 bg-gray-300 text-gray-700 py-4 rounded-full font-bold hover:bg-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                          style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                        >
                          Cancel
                        </button>
                      )}
                    </>
                  )}
                </div>
              </form>
            </motion.div>

            {/* Subscription Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-black text-green-500" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                  My Subscription
                </h2>
                <button
                  onClick={() => setIsAddServicePopupOpen(true)}
                  className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-4 py-2 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-sm"
                >
                  <i className="ri-add-line text-lg"></i>
                  Add Service
                </button>
              </div>

              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-xl font-bold text-gray-600">Loading subscription...</div>
                </div>
              ) : subscription ? (
                <div className="space-y-6">
                  {/* Current Subscription Card */}
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 border-4 border-green-500">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-black text-green-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                          {getPlanName(subscription.plan)}
                        </h3>
                        <div className="flex items-baseline gap-2 mt-2">
                          <span className="text-4xl font-black text-gray-900" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                            ${subscription.price}
                          </span>
                          <span className="text-lg text-gray-600">/{subscription.interval}</span>
                        </div>
                      </div>
                      <div className={`px-4 py-2 rounded-full font-bold text-sm ${
                        subscription.status === 'active' 
                          ? 'bg-green-500 text-white' 
                          : subscription.cancel_at_period_end
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-500 text-white'
                      }`}>
                        {subscription.cancel_at_period_end ? 'Canceling' : subscription.status.toUpperCase()}
                      </div>
                    </div>

                    <div className="space-y-3 text-gray-700">
                      <div className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                        <span>Up to 2 trash cans included</span>
                      </div>
                      {subscription.additional_cans && subscription.additional_cans > 0 && (
                        <div className="flex items-center gap-2">
                          <i className="ri-add-circle-fill text-blue-500 text-xl"></i>
                          <span className="font-bold">{subscription.additional_cans} additional can{subscription.additional_cans > 1 ? 's' : ''} (+${subscription.additional_cans * 10})</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <i className="ri-calendar-check-fill text-blue-600 text-xl"></i>
                        <span>Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}</span>
                      </div>
                      {subscription.cancel_at_period_end && (
                        <div className="flex items-center gap-2 text-yellow-700">
                          <i className="ri-alert-fill text-yellow-500 text-xl"></i>
                          <span className="font-bold">Will cancel on {new Date(subscription.current_period_end).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Upgrade/Change Plan Section */}
                  {!subscription.cancel_at_period_end && (
                    <div className="space-y-4">
                      <button
                        onClick={() => setShowModifyOptions(!showModifyOptions)}
                        className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-4 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <i className="ri-exchange-line text-xl"></i>
                        {showModifyOptions ? 'Hide Options' : 'Upgrade or Change Plan'}
                      </button>

                      {showModifyOptions && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 space-y-5 border-2 border-blue-200"
                        >
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              <i className="ri-price-tag-3-line mr-2"></i>
                              Select Plan
                            </label>
                            <div className="space-y-3">
                              {[
                                { id: 'monthly', name: 'Monthly Clean', price: 20, desc: 'Cleaned every month', savings: '' },
                                { id: 'quarterly', name: 'Quarterly Clean', price: 35, desc: 'Cleaned every 3 months', savings: 'Best Value' }
                              ].map((plan) => (
                                <button
                                  key={plan.id}
                                  onClick={() => setSelectedNewPlan(plan.id)}
                                  className={`w-full p-4 rounded-2xl border-3 font-bold transition-all cursor-pointer whitespace-nowrap text-left relative overflow-hidden ${
                                    selectedNewPlan === plan.id
                                      ? 'border-green-500 bg-green-50 shadow-lg'
                                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow'
                                  } ${subscription.plan === plan.id ? 'ring-2 ring-blue-400' : ''}`}
                                >
                                  {plan.savings && (
                                    <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs px-3 py-1 rounded-bl-lg font-bold">
                                      {plan.savings}
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{plan.name}</span>
                                        {subscription.plan === plan.id && (
                                          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Current</span>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-500 font-normal mt-1">{plan.desc}</div>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-3xl font-black text-gray-900" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>${plan.price}</span>
                                      <div className="text-xs text-gray-500">/{plan.id === 'monthly' ? 'mo' : 'qtr'}</div>
                                    </div>
                                  </div>
                                  {selectedNewPlan === plan.id && (
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                      <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                              <i className="ri-add-box-line mr-2"></i>
                              Additional Cans ($10 each)
                            </label>
                            <div className="flex items-center justify-center gap-4 bg-white rounded-2xl p-4 border-2 border-gray-100">
                              <button
                                onClick={() => setNewAdditionalCans(Math.max(0, newAdditionalCans - 1))}
                                disabled={newAdditionalCans === 0}
                                className={`w-12 h-12 rounded-full font-bold text-2xl flex items-center justify-center whitespace-nowrap cursor-pointer transition-all ${
                                  newAdditionalCans === 0 
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                                    : 'bg-red-100 hover:bg-red-200 text-red-600'
                                }`}
                              >
                                -
                              </button>
                              <div className="text-4xl font-black text-gray-900 w-16 text-center" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                                {newAdditionalCans}
                              </div>
                              <button
                                onClick={() => setNewAdditionalCans(newAdditionalCans + 1)}
                                className="w-12 h-12 rounded-full bg-green-100 hover:bg-green-200 text-green-600 font-bold text-2xl flex items-center justify-center whitespace-nowrap cursor-pointer transition-all"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {selectedNewPlan && (
                            <div className="bg-white p-5 rounded-2xl border-2 border-green-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-gray-600">Base Plan</span>
                                <span className="font-bold">${getPlanPrice(selectedNewPlan)}</span>
                              </div>
                              {newAdditionalCans > 0 && (
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-gray-600">{newAdditionalCans} Additional Can{newAdditionalCans > 1 ? 's' : ''}</span>
                                  <span className="font-bold">+${newAdditionalCans * 10}</span>
                                </div>
                              )}
                              <div className="border-t-2 border-dashed border-gray-200 my-3"></div>
                              <div className="flex items-center justify-between">
                                <span className="text-lg font-bold text-gray-800">New Total</span>
                                <div className="text-right">
                                  <div className="text-3xl font-black text-green-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                                    ${getPlanPrice(selectedNewPlan) + (newAdditionalCans * 10)}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    per {selectedNewPlan === 'monthly' ? 'month' : 'quarter'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <button
                            onClick={handleUpdateSubscription}
                            disabled={!selectedNewPlan || subscriptionLoading || (selectedNewPlan === subscription.plan && newAdditionalCans === (subscription.additional_cans || 0))}
                            className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-white py-4 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                          >
                            {subscriptionLoading ? (
                              <>
                                <i className="ri-loader-4-line animate-spin text-xl"></i>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="ri-check-line text-xl"></i>
                                Update Subscription
                              </>
                            )}
                          </button>
                          <p className="text-xs text-center text-gray-500">
                            Changes will be prorated on your next billing cycle
                          </p>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Cancel Subscription */}
                  {!subscription.cancel_at_period_end && (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={subscriptionLoading}
                      className="w-full bg-gray-200 text-gray-700 py-3 rounded-full font-bold hover:bg-red-100 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm"
                    >
                      {subscriptionLoading ? 'Processing...' : 'Cancel Subscription'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 space-y-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-yellow-200 to-yellow-400 rounded-full mx-auto flex items-center justify-center">
                    <i className="ri-calendar-check-line text-6xl text-yellow-700"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">No Active Subscription</h3>
                    <p className="text-gray-600 mb-6">Start a subscription to save money and keep your cans clean all year!</p>
                  </div>
                  <button
                    onClick={handleStartSubscription}
                    className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                  >
                    <i className="ri-shopping-cart-line text-xl"></i>
                    Browse Subscription Plans
                  </button>
                </div>
              )}
            </motion.div>
          </div>

          {/* Payment & Subscription History Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-8 shadow-2xl"
          >
            <h2 className="text-3xl font-black text-orange-500 mb-6" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              <i className="ri-history-line mr-2"></i>
              Service History
            </h2>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-full w-fit">
              <button
                onClick={() => setActiveHistoryTab('payments')}
                className={`px-6 py-2 rounded-full font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeHistoryTab === 'payments'
                    ? 'bg-white text-orange-600 shadow'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <i className="ri-bank-card-line mr-2"></i>
                Payments
              </button>
              <button
                onClick={() => setActiveHistoryTab('subscriptions')}
                className={`px-6 py-2 rounded-full font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeHistoryTab === 'subscriptions'
                    ? 'bg-white text-orange-600 shadow'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <i className="ri-calendar-line mr-2"></i>
                Subscriptions
              </button>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-xl font-bold text-gray-600">Loading history...</div>
              </div>
            ) : activeHistoryTab === 'payments' ? (
              /* Payment History */
              paymentHistory.length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-orange-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          payment.type === 'subscription' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <i className={`text-2xl ${
                            payment.type === 'subscription' 
                              ? 'ri-repeat-line text-green-600' 
                              : 'ri-shopping-bag-line text-blue-600'
                          }`}></i>
                        </div>
                        <div>
                          <div className="font-bold text-gray-800">{payment.plan}</div>
                          <div className="text-sm text-gray-500">{formatDate(payment.date)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-xl font-black text-gray-900" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                            ${payment.amount.toFixed(2)}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-bold ${getStatusColor(payment.status)}`}>
                            {payment.status.toUpperCase()}
                          </span>
                        </div>
                        {payment.invoiceUrl && (
                          <a
                            href={payment.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full transition-colors cursor-pointer"
                            title="View Invoice"
                          >
                            <i className="ri-external-link-line text-gray-600"></i>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto flex items-center justify-center mb-4">
                    <i className="ri-file-list-3-line text-4xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500">No payment history yet</p>
                </div>
              )
            ) : (
              /* Subscription History */
              subscriptionHistory.length > 0 ? (
                <div className="space-y-3">
                  {subscriptionHistory.map((sub, index) => (
                    <div
                      key={sub.id}
                      className={`p-4 rounded-2xl border-2 transition-shadow hover:shadow-md ${
                        index === 0 && sub.status === 'active' 
                          ? 'bg-gradient-to-r from-green-50 to-teal-50 border-green-300' 
                          : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            sub.status === 'active' ? 'bg-green-200' : 'bg-gray-200'
                          }`}>
                            <i className={`text-2xl ${
                              sub.status === 'active' 
                                ? 'ri-checkbox-circle-fill text-green-600' 
                                : 'ri-time-line text-gray-500'
                            }`}></i>
                          </div>
                          <div>
                            <div className="font-bold text-gray-800 flex items-center gap-2">
                              {sub.plan}
                              {index === 0 && sub.status === 'active' && (
                                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Current</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              Started {formatDate(sub.startDate)}
                              {sub.endDate && ` • Ended ${formatDate(sub.endDate)}`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm px-3 py-1 rounded-full font-bold ${getStatusColor(sub.status)}`}>
                            {sub.status.toUpperCase()}
                          </span>
                          {sub.status === 'active' && (
                            <div className="text-xs text-gray-500 mt-1">
                              Renews {formatDate(sub.currentPeriodEnd)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto flex items-center justify-center mb-4">
                    <i className="ri-calendar-check-line text-4xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500">No subscription history yet</p>
                </div>
              )
            )}
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Add Service Popup */}
      <AddServicePopup
        isOpen={isAddServicePopupOpen}
        onClose={() => setIsAddServicePopupOpen(false)}
        userEmail={profile?.email || ''}
      />
    </div>
  );
}